import pipe from 'callbag-pipe';
import subscribe from 'callbag-subscribe';

import makeBehaviorSubject from 'callbag-behavior-subject';
import {ApiService} from '../api';
import {ShopifyVariant} from '../../interfaces/shopify-product.interface';
import {URLS, UrlUtils} from '../urls';
import {Planogram} from '../../planogram';
import {
  CheckoutVariant,
  ErrorResponseInterface,
  EventType,
  ShoppingCartInterface,
  ShoppingCartVariant
} from '../../interfaces/shopping-cart.interface';
import {L10nUtils} from '../../utils/l10n_utils';
import {ShoppingAccount} from '../../shopping-account/account';
import {CurrencyService} from './currency.service';
import {SHOPPING_ACCESS_TOKEN_KEY, CURRENCY_CODE_KEY, SHOPIFY_MULTIPASS_TOKEN_KEY} from '../../shared/constants';
import Router from '../../router';
import {AppUtils} from '../../utils/app_utils';
import {ShoppingCartUtils} from '../../utils/shopping-cart_utils';
import {ShoppingCartBaseInterface} from '../../interfaces/shopping-cart.interface';

// 1 minute is equal to 60secs and 60secs is equal to 60 * 1000 = 60000ms
const SHOPPING_CART_UPDATE_INTERVAL = 60 * 1000;

const SHOPPING_CART_TIME_THRESHOLD = 30 * 60 * 1000;

export class ShoppingCartService extends ApiService implements ShoppingCartBaseInterface {
  checkoutSubject: makeBehaviorSubject<object>;
  processingSubject: makeBehaviorSubject<boolean>;
  shoppingCart: ShoppingCartInterface;
  isMultipassEnabled: boolean;
  email: string;
  cancelAnimationFn: Function;
  private readonly cartId: string;
  private readonly mutlipassId: string;
  private readonly checkoutSubjectRef: Function;
  static isProcessing: boolean;

  constructor(private planogram: Planogram, private currencyService: CurrencyService) {
    super();
    this.onShoppingCartStorageChange = this.onShoppingCartStorageChange.bind(this);
    this.cartId = `checkout-${this.planogram.clientName}`;
    this.mutlipassId = `multipass_token-${this.planogram.clientName}`;
    const storedCheckoutState = ShoppingCartUtils.getStoredShoppingCart(this.cartId);
    this.checkoutSubject = makeBehaviorSubject(JSON.parse(storedCheckoutState) || null);
    this.processingSubject = makeBehaviorSubject(false);

    window.addEventListener('storage', this.onShoppingCartStorageChange);

    this.checkoutSubjectRef = pipe(
      this.checkoutSubject,
      subscribe({
        next: (data: ShoppingCartInterface) => {
          if (data && !data.last_update) {
            data = {...data, last_update: new Date().getTime()};
          }
          this.shoppingCart = data;
        },
        error: () => {
          this.shoppingCart = null;
        }
      })
    );
  }

  get getMultipassToken() {
    return window.localStorage.getItem(this.mutlipassId);
  }

  fireShoppingCartUpdates(variant: ShopifyVariant): Promise<ShoppingCartInterface | void> {
    return this.shoppingCart ? this.updateShoppingCart(variant) : this.createShoppingCart(variant);
  }

  attachShoppingCartToUser() {
    const requestData = {
      cart: this.shoppingCart.products.map((product: ShoppingCartVariant) => {
        return {
          merchandiseId: product.merchandise_id,
          quantity: product.quantity,
          attributes: product.attributes
        };
      })
    };
    this.processingSubject(EventType.Data, true);
    return this.createCartRequest(requestData)
      .then(() => ShoppingCartUtils.deleteStoredShoppingCart(this.cartId))
      .finally(() => this.processingSubject(EventType.Data, false));
  }

  updateShoppingCartQuantity(variant: ShoppingCartVariant): Promise<ShoppingCartInterface> {
    const indexOfVariant = this.getIndexVariant(variant);
    if (indexOfVariant !== -1) {
      this.shoppingCart.cart[indexOfVariant].quantity = variant.quantity;
    }
    this.processingSubject(EventType.Data, true);
    return this.updateCartRequest(this.shoppingCart.cart[indexOfVariant])
      .then(data => this.saveShoppingCartState(data))
      .catch(err => this.clearShoppingCartState(err))
      .finally(() => this.processingSubject(EventType.Data, false));
  }

  clearShoppingCartState(err?: ErrorResponseInterface) {
    this.checkoutSubject(EventType.End, err);
    ShoppingCartUtils.deleteStoredShoppingCart(this.cartId);
    if (err) {
      throw err;
    }
  }

  clearShoppingCartToken() {
    this.isMultipassEnabled = false;
    window.localStorage.removeItem(this.mutlipassId);
  }

  removeVariant(variantId: string) {
    return this.put(URLS.SHOPIFY_REMOVE_ITEM, {
      cart_lines: [{id: variantId}],
      cart_id: this.shoppingCart.checkout_id
    }).then(resp => resp.json());
  }

  removeItemFromCart(variant: ShopifyVariant): Promise<ShoppingCartInterface> {
    const indexOfVariant = this.getIndexVariant(variant);
    this.shoppingCart.cart.splice(indexOfVariant, 1);
    this.processingSubject(EventType.Data, true);
    return this.removeVariant(variant.id.toString())
      .then(data => this.saveShoppingCartState(data))
      .catch(err => this.clearShoppingCartState(err))
      .finally(() => this.processingSubject(EventType.Data, false));
  }

  generateMultipassLink(link: string): Promise<{link: string}> {
    const url = UrlUtils.insertFewValuesToUrl(URLS.SHOPIFY_GENERATE_MULTIPASS_LINK, {
      token: this.getMultipassToken,
      link
    });

    return this.get(url).then(resp => resp.json());
  }

  private createCartRequest(data): Promise<ShoppingCartInterface> {
    const currentLang = L10nUtils.getCurrentLanguage();
    const url = UrlUtils.insertFewValuesToUrl(URLS.SHOPIFY_CHECKOUT_CREATE, {
      lang: currentLang,
      currency_code: this.currencyService.selectedCurrencyCode
    });

    return this.post(url, data).then(resp => resp.json());
  }

  private updateCartRequest(variant: CheckoutVariant): Promise<ShoppingCartInterface> {
    const currentLang = L10nUtils.getCurrentLanguage();
    const url = UrlUtils.insertFewValuesToUrl(URLS.SHOPIFY_CHECKOUT_UPDATE, {
      lang: currentLang,
      currency_code: this.currencyService.selectedCurrencyCode
    });

    return this.put(url, {
      cart_lines: [variant],
      cart_id: this.shoppingCart.checkout_id
    }).then(resp => resp.json());
  }

  private addItemCartRequest(variant: CheckoutVariant): Promise<ShoppingCartInterface> {
    const currentLang = L10nUtils.getCurrentLanguage();
    const url = UrlUtils.insertFewValuesToUrl(URLS.SHOPIFY_CHECKOUT_ADD_ITEM, {
      lang: currentLang,
      currency_code: this.currencyService.selectedCurrencyCode
    });

    return this.put(url, {
      cart: [variant],
      cart_id: this.shoppingCart.checkout_id
    }).then(resp => resp.json());
  }

  private createShoppingCart(variant: ShopifyVariant): Promise<ShoppingCartInterface> {
    const requestData = {
      cart: [
        {
          merchandiseId: variant.variant_id,
          quantity: variant.quantity,
          attributes: variant.attributes
        }
      ]
    };
    this.processingSubject(EventType.Data, true);
    return this.createCartRequest(requestData)
      .then(data => this.saveShoppingCartState(data))
      .catch(err => this.clearShoppingCartState(err))
      .finally(() => this.processingSubject(EventType.Data, false));
  }

  private updateShoppingCart(variant: ShopifyVariant): Promise<ShoppingCartInterface> {
    const indexOfVariant = this.getIndexVariant(variant);
    const overallQuantity = AppUtils.overallQuantity(this.shoppingCart, variant, variant?.per_order_limit);
    let availableQuantity = variant.inventory_quantity - overallQuantity;
    this.processingSubject(EventType.Data, true);
    if (indexOfVariant !== -1) {
      if (variant.per_order_limit) {
        availableQuantity = variant.per_order_limit - overallQuantity;
        this.shoppingCart.cart[indexOfVariant].quantity = Math.min(
          this.shoppingCart.cart[indexOfVariant].quantity + variant.quantity,
          availableQuantity < variant.per_order_limit
            ? this.shoppingCart.cart[indexOfVariant].quantity + availableQuantity
            : variant.per_order_limit
        );
      } else {
        this.shoppingCart.cart[indexOfVariant].quantity = Math.min(
          this.shoppingCart.cart[indexOfVariant].quantity + variant.quantity,
          availableQuantity < variant.inventory_quantity
            ? this.shoppingCart.cart[indexOfVariant].quantity + availableQuantity
            : variant.inventory_quantity
        );
      }
      (this.shoppingCart.cart[indexOfVariant] as any).attributes = variant.attributes;

      return this.updateCartRequest(this.shoppingCart.cart[indexOfVariant])
        .then(data => this.saveShoppingCartState(data))
        .catch(err => this.clearShoppingCartState(err))
        .finally(() => this.processingSubject(EventType.Data, false));
    } else {
      if (variant.per_order_limit) {
        availableQuantity = variant.per_order_limit - overallQuantity;
      }
      const shoppingCartVariant = {
        merchandiseId: variant.variant_id,
        quantity: variant.quantity > availableQuantity ? availableQuantity : variant.quantity,
        attributes: variant.attributes
      } as CheckoutVariant;
      this.shoppingCart.cart.push(shoppingCartVariant);

      return this.addItemCartRequest(shoppingCartVariant)
        .then(data => this.saveShoppingCartState(data))
        .catch(err => this.clearShoppingCartState(err))
        .finally(() => this.processingSubject(EventType.Data, false));
    }
  }

  private onShoppingCartStorageChange({storageArea, key, newValue}: StorageEvent) {
    if (storageArea === window.localStorage && key === this.cartId) {
      if (newValue) {
        const cart = JSON.parse(newValue) as ShoppingCartInterface;
        this.saveShoppingCartState({cart});
      } else {
        this.clearShoppingCartState();
      }
    } else if (storageArea === window.localStorage && key === SHOPPING_ACCESS_TOKEN_KEY && !newValue) {
      this.clearShoppingCartState();
    } else if (
      storageArea === window.localStorage &&
      key === CURRENCY_CODE_KEY &&
      newValue &&
      ShoppingAccount.isLogged
    ) {
      this.updateShoppingCartCurrency(newValue);
    }
  }

  private getIndexVariant(variant: ShopifyVariant) {
    return this.shoppingCart.cart?.findIndex(product => {
      if (!variant.attributes?.length && !product.attributes?.length) {
        return product.merchandiseId === variant.merchandise_id || product.merchandiseId === variant.variant_id;
      }

      if (product.attributes === undefined || variant.attributes === undefined) {
        return product.merchandiseId === variant.merchandise_id || product.merchandiseId === variant.variant_id;
      }

      return Array.from(product.attributes).every(({key, value}, index) => {
        return key === variant.attributes?.[index]?.key && value === variant.attributes?.[index]?.value;
      });
    });
  }

  checkMultipassToken(): Promise<void> {
    const currentUrl = new URL(window.location.href);
    const multipassToken = currentUrl.searchParams.get(SHOPIFY_MULTIPASS_TOKEN_KEY) || this.getMultipassToken;

    if (multipassToken) {
      const url = UrlUtils.insertValueToUrl(URLS.SHOPIFY_VERIFY_MULTIPASS_TOKEN, multipassToken);

      return this.get(url)
        .then(resp =>
          resp.json().then(data => {
            this.isMultipassEnabled = !!data.status.status && this.planogram.isMultipassKeyAvailable;

            if (this.isMultipassEnabled) {
              window.localStorage.setItem(this.mutlipassId, multipassToken);
            } else {
              window.localStorage.removeItem(this.mutlipassId);
            }

            return data;
          })
        )
        .catch(err => {
          console.warn(err);
          this.clearShoppingCartToken();
        })
        .finally(() => Router.removeParamFromUrl(SHOPIFY_MULTIPASS_TOKEN_KEY));
    }

    return Promise.resolve();
  }

  saveShoppingCartState(data) {
    const checkoutProducts = data.cart?.products?.map((product: ShoppingCartVariant) => {
      return {
        id: product.id,
        merchandiseId: product.merchandise_id,
        quantity: product.quantity,
        attributes: product.attributes
      };
    });
    const parsedData = {...data.cart, cart: checkoutProducts, checkout_id: decodeURIComponent(data.cart.checkout_id)};
    this.checkoutSubject(EventType.Data, parsedData);
    if (!ShoppingAccount.isLogged || this.isMultipassEnabled) {
      this.updateShoppingCartState();
      parsedData.last_update = new Date().getTime();
      window.localStorage.setItem(this.cartId, JSON.stringify(parsedData));
    }
    return parsedData;
  }

  storeEmail(email: string | undefined) {
    if (email) {
      this.email = email;
    }
  }

  updateShoppingCartCurrency(currencyCode: string) {
    if (!this.shoppingCart) {
      return Promise.resolve();
    }
    const url = UrlUtils.insertFewValuesToUrl(URLS.SHOPIFY_CHECKOUT_UPDATE_CURRENCY, {
      currency_code: currencyCode,
      lang: L10nUtils.getCurrentLanguage()
    });

    this.processingSubject(EventType.Data, true);
    ShoppingCartService.isProcessing = true;
    return this.put(url, {cart_id: this.shoppingCart.checkout_id})
      .then(resp => resp.json())
      .then(data => this.saveShoppingCartState(data))
      .catch(err => this.clearShoppingCartState(err))
      .finally(() => {
        this.processingSubject(EventType.Data, false);
        ShoppingCartService.isProcessing = false;
      });
  }

  isShoppingCartOutdated() {
    if (!ShoppingAccount.isLogged && this.shoppingCart?.last_update) {
      const currentTime = new Date().getTime();
      return this.shoppingCart.last_update + SHOPPING_CART_TIME_THRESHOLD < currentTime;
    }

    return false;
  }

  private trackShoppingCartUpdates() {
    AppUtils.requestTimeoutRaf(
      () => this.updateShoppingCartState(),
      SHOPPING_CART_UPDATE_INTERVAL,
      fn => (this.cancelAnimationFn = fn)
    );
  }

  updateShoppingCartState(forceUpdate: boolean = false) {
    this.clearShoppingCartTimer();
    if (
      (!ShoppingAccount.isLogged && this.isShoppingCartOutdated()) ||
      (forceUpdate && !ShoppingCartService.isProcessing)
    ) {
      this.updateShoppingCartCurrency(this.shoppingCart?.currency_code).finally(() => this.trackShoppingCartUpdates());
      return;
    } else if (this.shoppingCart) {
      this.trackShoppingCartUpdates();
    }
  }

  clearShoppingCartTimer() {
    this.cancelAnimationFn?.();
  }

  dispose() {
    window.removeEventListener('storage', this.onShoppingCartStorageChange);
    this.checkoutSubject(EventType.End, true);
    this.processingSubject(EventType.End, true);
    this.checkoutSubjectRef();
  }
}
